import React, { useState, useContext, useEffect } from 'react';

import { getFirestore, collection, query, orderBy, where, onSnapshot, doc, getDoc, getDocs, deleteDoc,addDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db, CATEGORIES } from '../config';
import { signOut, sendPasswordResetEmail  } from 'firebase/auth';

import Acknowledgement from '../components/Onboarding/Acknowledgement';
import OnboardingFestival from '../components/Onboarding/OnboardingFestival';
import Loading from '../components/UI/Loading';
import Home from '../components/Home/Home';
import Checklist from '../components/Checklist/Checklist';
import Notifications from '../components/Notifications/Notifications';
import Settings from '../components/Settings/Settings';
import BottomTabs from '../components/Navigation/BottomTabs';
import TopMenu from '../components/Navigation/TopMenu';

import { UserContext } from '../store/user-context';
import { generateId } from '../config/helpers';
import NeedScreen from '../components/Checklist/NeedScreen';
import PackScreen from '../components/Checklist/PackScreen';
import AddSheet from '../components/Checklist/AddSheet';
import MessageModal from '../components/Modals/MessageModal';

// Accessible nav colour — the bottom nav's active icon/label needs enough
// contrast against its white-ish background regardless of a festival's own
// brand colour, since a light/bright brand colour (chosen for vibrancy
// elsewhere in the UI) can otherwise be unreadable there. These three
// helpers derive a WCAG-ish 3:1-against-white variant of --brand-primary
// for that one use, without touching the brand colour used everywhere else.
function getLuminance(hexColor) {
  const rgb = hexColor.replace('#', '').match(/.{2}/g).map(c => {
    const val = parseInt(c, 16) / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function getContrastRatio(hexColor) {
  // Contrast against white (luminance = 1)
  return (1 + 0.05) / (getLuminance(hexColor) + 0.05);
}

// Perceived brightness (ITU-R BT.601 "YIQ" weighting) — deliberately NOT
// getLuminance/WCAG relative luminance above. That formula weights green at
// 0.7152 and red at only 0.2126, so a vivid, low-green magenta/pink (e.g.
// #f13b8a) scores as "dark" even though it reads as a bright, vivid colour
// to the eye — recommending black text where white is visibly more
// readable (confirmed against Beyond The Valley's #f13b8a brand colour,
// which getAccessibleTextColour below used to get backwards). This
// weighting (299/587/114, simple 0-255 average, no gamma correction) is the
// standard, widely-used heuristic for exactly this "pick black or white
// text for a background" decision, and tracks perceived brightness far
// more closely for saturated reds/magentas.
function getPerceivedBrightness(hexColor) {
  const rgb = hexColor.replace('#', '').match(/.{2}/g).map(c => parseInt(c, 16));
  return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
}

// Picks plain white or black text for a given background colour — for
// surfaces that use --brand-primary itself as a background (e.g.
// NeedScreen's swipe action buttons), where a bright/light brand colour
// needs dark text and a deep/dark one needs white, rather than
// ensureAccessibleNavColour's approach of darkening the colour to stay
// legible against a fixed white background. Threshold of 128 is the
// standard midpoint of the 0-255 brightness range.
function getAccessibleTextColour(hexColor) {
  return getPerceivedBrightness(hexColor) >= 128 ? '#000000' : '#ffffff';
}

function darkenColour(hex, amount) {
  const rgb = hex.replace('#', '').match(/.{2}/g)
    .map(c => Math.max(0, Math.floor(parseInt(c, 16) * (1 - amount))));
  return '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');
}

function ensureAccessibleNavColour(hexColor) {
  let colour = hexColor;
  let ratio = getContrastRatio(colour);
  let attempts = 0;
  // Darken by 10% increments until contrast ratio >= 3:1
  while (ratio < 3 && attempts < 10) {
    colour = darkenColour(colour, 0.1);
    ratio = getContrastRatio(colour);
    attempts++;
  }
  return colour;
}

const Root = props => {

  const [groups, setGroups] = useState();
  const [currentScreen, setCurrentScreen] = useState('home');
  const [activeCategory, setActiveCategory] = useState('');
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [addSheetInitialMode, setAddSheetInitialMode] = useState('menu');
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // Pack tab bump + count badge when an item is sorted from Need (see
  // NeedScreen.js's handleMoveToPack) — session-only, not persisted.
  // packBumpTrigger is a plain incrementing counter BottomTabs watches for
  // *changes* (not the value itself) to replay its bump animation each time.
  const [packSortBadge, setPackSortBadge] = useState(0);
  const [packBumpTrigger, setPackBumpTrigger] = useState(0);
  const handleSortedToPack = () => {
    setPackSortBadge(c => c + 1);
    setPackBumpTrigger(c => c + 1);
  };
  useEffect(() => {
    if (currentScreen === 'pack') setPackSortBadge(0);
  }, [currentScreen]);
  const addSheetCategoryRef = React.useRef('');

  const handleShowToast = (msg) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  const handleAddCategory = () => {
    setAddSheetInitialMode('category');
    setShowAddSheet(true);
    setAddSheetCategory('');
    addSheetCategoryRef.current = '';
  };

  const handleRenameCategory = async (oldName, newName) => {
    if (!group || !newName || newName === oldName) return;
    const updatedCategories = group.categories.map(c => c === oldName ? newName : c);
    // Move the icon entry to the new name rather than leaving it keyed
    // under the name that no longer exists — this is what makes a
    // category's icon survive a rename instead of falling back to the
    // puzzle-piece default (see categoryIcons.js).
    const updatedIcons = { ...(group.categoryIcons || {}) };
    if (oldName in updatedIcons) {
      updatedIcons[newName] = updatedIcons[oldName];
      delete updatedIcons[oldName];
    }
    await updateDoc(doc(db, 'groups', group.id), { categories: updatedCategories, categoryIcons: updatedIcons });
  };

  // Confirmation is handled by the manage sheet itself (CategoryManageSheet.js)
  // before this is called — no window.confirm here too.
  const handleDeleteCategory = async (category) => {
    if (!group) return;
    const updatedCategories = group.categories.filter(c => c !== category);
    const updatedIcons = { ...(group.categoryIcons || {}) };
    delete updatedIcons[category];
    await updateDoc(doc(db, 'groups', group.id), { categories: updatedCategories, categoryIcons: updatedIcons });
  };

  const handleSetCategoryIcon = async (category, iconKey) => {
    if (!group) return;
    const updatedIcons = { ...(group.categoryIcons || {}), [category]: iconKey };
    await updateDoc(doc(db, 'groups', group.id), { categoryIcons: updatedIcons });
  };

  const handleReorderCategories = async (newCategories) => {
    if (!group) return;
    await updateDoc(doc(db, 'groups', group.id), { categories: newCategories });
  };

  const [addSheetCategory, setAddSheetCategory] = useState('');
  const [group, setGroup] = useState();
  const [itemsLoadFailed, setItemsLoadFailed] = useState(false);
  const [working, setWorking] = useState(false);
  const [settings, setSettings] = useState();
  const [festival, setFestival] = useState();
   
  
  const UserCtx = useContext(UserContext);
  //console.log(UserCtx.itemInfo)
 
  const user = auth.currentUser;

 //console.log(user.email)

  const addNewGroup = async (user, userName) => {
    //called when user creates a new group / event from settings screen
    // remember the group that was active before this draft, so the
    // onboarding screen can offer a way back to it (see OnboardingFestival)
    if (group?.id) localStorage.setItem('lastActiveGroupId', group.id);
     const newGroup = {
      acknowledgement:true,
      id: '123',
      owner: user.uid,
      memberIds: [user.uid], //member ids only for queries later on
      memberEmails: [user.email], //member emails only for queries later on
      categories: CATEGORIES,
      members: [
        {
          id: user.uid,
          email:user.email, 
          name:userName, 
          avatar: 1,
          editor: true,
          owner: true
        }
      ], //set up membership for group
      createdAt: Timestamp.fromDate(new Date())
    }
    //add new group to group collection
    try {
      const newGroupRef = await addDoc(collection(db, 'groups'), newGroup);
      //add the auto generated id to the created group
      if (newGroupRef && newGroupRef.id) {
        const groupRef = doc(db, 'groups', newGroupRef.id);
        await updateDoc(groupRef, { id: newGroupRef.id});
        newGroup.id=newGroupRef.id;
        setGroup(newGroup);
        UserCtx.setCurrent(newGroupRef.id);
      }
      return true;
    } catch (err) {
      alert(err.message);
      return false;
    }
}

  useEffect(() => {
    //set up listener for groups
    //Firestore database query snapshot https://firebase.google.com/docs/firestore/query-data/listen
    if (!user) return;
    setGroups();
    UserCtx.setGroups()
    //console.log('Listening for groups for',user.uid);
    //A maximum of 1 'ARRAY_CONTAINS' filter is allowed per disjunction.
    const q = (user.isAnonymous) ? 
      query(
        collection(db, 'groups'), 
        where("memberIds", "array-contains", user.uid),
        ) : 
      query(
        collection(db, 'groups'), 
        where("memberEmails", "array-contains", user.email),
        );
    const unsubscribe1 = onSnapshot(q,
    (querySnapshot) => {
      const groupsSnapshot = [];
      querySnapshot.forEach((doc) => {
      groupsSnapshot.push(doc.data());
      });
      setGroups(groupsSnapshot);
      UserCtx.setGroups(groupsSnapshot)
    },
    (error) => {
      console.log(error.message);
    });
    //set up listener for user settings
    //console.log('Listening for user settings for',user.uid);
    const unsubscribe2 = onSnapshot(doc(db, 'users', user.uid), (doc) => {
        if (doc.data()) {
          setSettings(doc.data());
          UserCtx.setSettings(doc.data())
        }
    });
    return () => {
      //console.log('Detaching the database listeners for',user.uid);
      unsubscribe1();
      unsubscribe2();
      setGroups();
      setSettings();
    };
  }, [user])

  useEffect(() => {
    if (!groups) return;
    if (groups.length > 0) {
      //load last loaded event (if they belong to more than one event group)
      const currentGroup = 
      (UserCtx.current) ? 
        //has previously logged in therefore current group has been set
        groups.find( group => group.id === UserCtx.current)  ?? groups[0]
      :
        //set current group to the first group they belong to
        groups[0]
        ;
      //console.log('groups have loaded, set current group to',UserCtx.current,currentGroup?.id);
      if (currentGroup) UserCtx.setCurrent(currentGroup.id);
      setGroup(currentGroup);
    } else {
      // No groups found — trigger creation of a new one rather than hanging on Loading
      UserCtx.setCurrent('new');
    }
  }, [groups])

  // Keep activeCategory in sync with group categories
  useEffect(() => {
    if (!group?.categories) return;
    // If the active category was deleted, fall back to All ('')
    if (activeCategory && !group.categories.includes(activeCategory)) {
      setActiveCategory('');
    }
  }, [group]);

  // Need and Pack always start on "All" — whatever category was last active
  // on My Gear (or wherever) shouldn't silently filter Need/Pack down to a
  // view that can look empty even though other categories have items. My
  // Gear itself is deliberately excluded, so browsing a category there
  // survives a quick trip to another tab and back.
  //
  // This is passed down as `setCurrentScreen` in place of the raw state
  // setter — every consumer already just calls setCurrentScreen(id), so no
  // child needs to change — rather than resetting activeCategory from a
  // useEffect keyed on currentScreen. NeedScreen/PackScreen each capture
  // activeCategory into a ref on their first render (to restore it once a
  // search clears); an effect-based reset would still let that first
  // render see the stale category before the correction lands one render
  // later, poisoning that ref. Resetting in the same synchronous update as
  // the screen change (React 18 batches both into one render) means they
  // never mount with the stale value to begin with.
  const goToScreen = (screen) => {
    if (screen === 'need' || screen === 'pack') setActiveCategory('');
    setCurrentScreen(screen);
  };

  useEffect(() => {
    if (UserCtx.current && groups && (UserCtx.current ==='new') ) {
      //add a new group
      addNewGroup(user,UserCtx.settings?.name);
    } else if (UserCtx.current && groups && (UserCtx.current !== group?.id) && (UserCtx.current !== 'delete') ) {
      //new group has been selected - switch to new group
      console.log('new group has been selected - switch to new group ',UserCtx.current);
      const currentGroup = groups.find( group => group.id === UserCtx.current);
      // Only update if found — avoids overwriting a valid group with undefined
      // when a stale localStorage ID doesn't match any current group
      if (currentGroup) {
        setGroup(currentGroup);
        setCurrentScreen('home');
      }
    } else if (UserCtx.current && groups && (UserCtx.current ==='delete') ) {
      if (groups.length > 0) {
        //switch to their next group
        console.log('current group has been deleted, switch to the next group',groups[0].id);
        UserCtx.setCurrent(groups[0].id);
      } else {
        //they've deleted their only group - add a new one
        console.log('current group has been deleted, no other groups available, add a new group');
        addNewGroup(user,UserCtx.settings?.name);
      }
    } 
  }, [UserCtx.current])
  

  // Auto-populate items from festival (or defaultitems) when a group has none
  useEffect(() => {
    if (!group?.festival || group.items != null) return;
    setItemsLoadFailed(false);
    const populateItems = async () => {
      let itemsToSave = [];

      // Sort by defaultitems' own curated `order` field first (items
      // without one, or explicitly 999, sort to the bottom), then
      // reassign a clean 0-based `order` reflecting that sequence. Only
      // items explicitly curated onto the minimal list (default: true) go
      // into a brand-new group's starting checklist — everything else is
      // still in the catalog for manual adding, just not pre-populated.
      const loadFromDefaultItems = async () => {
        const querySnapshot = await getDocs(collection(db, 'defaultitems'));
        const allItems = [];
        querySnapshot.forEach(d => allItems.push(d.data()));
        const minimalItems = allItems.filter(item => item.default === true);
        const sortedItems = [...minimalItems].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        return sortedItems.map((item, index) => ({
          ...item,
          id: item.id || generateId(20),
          organise: [{ assigned: user.uid, checked: false }],
          ownQuantity: 0,
          needQuantity: 0,
          order: index,
        }));
      };

      try {
        if (group.festival.id) {
          const docSnap = await getDoc(doc(db, 'festivals', group.festival.id));
          const festivalData = docSnap.exists() ? docSnap.data() : null;
          const festivalItems = festivalData?.items;
          const useDefaults = !!festivalData?.useDefaultItems || !festivalItems || festivalItems.length === 0;

          if (useDefaults) {
            itemsToSave = await loadFromDefaultItems();
          } else {
            // Match festival items (plain array entries, no stable doc ID)
            // against defaultitems' curated `order` by name (case-insensitive,
            // trimmed). An item that already has its own `order` (an admin
            // override) keeps it; anything with no match (festival-specific
            // extras) falls back to 999, same as unmatched defaultitems.
            //
            // This branch used to also alias a handful of pre-rename item
            // names (e.g. 'rainjacket' -> 'rain jacket', 'mattress' ->
            // 'sleeping mat') before the name lookup, since renaming a
            // defaultitems entry never touched the copies already embedded
            // on festival docs. That workaround is gone now — festivals
            // should be migrated to useDefaultItems: true (see
            // scripts/migrate-to-default-items.js), which sources fresh from
            // defaultitems above instead of using a stale embedded copy.
            const defaultItemsSnap = await getDocs(collection(db, 'defaultitems'));
            const orderByName = new Map();
            defaultItemsSnap.forEach(d => {
              const data = d.data();
              const key = (data.name || '').trim().toLowerCase();
              if (key && !orderByName.has(key)) orderByName.set(key, data.order ?? 999);
            });

            const resolveOrder = (item) => {
              if (item.order !== undefined) return item.order;
              const key = (item.name || '').trim().toLowerCase();
              return orderByName.has(key) ? orderByName.get(key) : 999;
            };

            // Same "default: true only" filter loadFromDefaultItems applies
            // to the shared pool — a customised festival's items array holds
            // everything an admin has made available (the full customise
            // table), not just what a brand-new user's starter list should
            // contain. Without this, customising even one item's tip/hire
            // silently gave every new user all 65 items instead of the lean
            // starter set the festival was actually curated for.
            const minimalFestivalItems = festivalItems.filter(item => item.default === true);
            const sortedItems = [...minimalFestivalItems].sort((a, b) => resolveOrder(a) - resolveOrder(b));

            itemsToSave = sortedItems.map((item, index) => ({
              ...item,
              // Festival items are plain array entries embedded on the
              // festival doc with no stable ID of their own — GearCard/
              // NeedScreen/PackScreen all key their own updates off item.id,
              // so without this every Own/Need tap on such an item silently
              // no-ops (the id-based lookup never matches, no error shown).
              id: item.id || generateId(20),
              organise: [{ assigned: user.uid, checked: false }],
              ownQuantity: 0,
              needQuantity: 0,
              order: index,
            }));
          }
        } else {
          itemsToSave = await loadFromDefaultItems();
        }
      } catch (err) {
        console.log('Error populating items:', err);
      }
      try {
        const groupRef = doc(db, 'groups', group.id);
        await updateDoc(groupRef, { items: itemsToSave });
      } catch (err) {
        console.error('Failed to save items, proceeding with empty list:', err);
        // Write failed — the snapshot will never update, so release the loading gate
        setItemsLoadFailed(true);
      }
    };
    populateItems();
  }, [group]);

  // Deliberately keyed on group?.festival?.id, not the whole `group` object.
  // `group` gets a new object reference on every Firestore group write —
  // including something as small as a GearCard stepper click — even though
  // the festival itself hasn't changed. Depending on the whole object made
  // this effect re-run on every such write, clearing `festival` to
  // undefined via setFestival() below and only restoring it a moment later
  // once the freshly re-subscribed listener's snapshot arrived — visible as
  // anything derived from festivalInfo (e.g. GearCard's hire badge, gated
  // on festivalInfo?.hireurl) flickering off and back on.
  useEffect(() => {
    //UserCtx.setCurrent(); signOut(auth).catch(error => console.log('Error logging out: ', error));
    //group's festival has changed
    setFestival();
    if (!group) return;
    if (!group.festival || !group.festival.id) {
      // No *real* linked festival — either a genuine custom event
      // (group.festival = {custom: true, ...}, no id — see EditFestival.js)
      // or a brand-new group still mid-onboarding. Reset brand colours to
      // the app defaults rather than leaving a previous festival's colours
      // applied. Checking group.festival.id (not just group.festival) matters
      // because a custom event's festival object is truthy but has no id.
      document.documentElement.style.setProperty('--brand-primary', '#2be494');
      document.documentElement.style.setProperty('--brand-secondary', '#2c3030');
      document.documentElement.style.setProperty('--brand-nav', ensureAccessibleNavColour('#2be494'));
      document.documentElement.style.setProperty('--brand-on-primary', getAccessibleTextColour('#2be494'));
    }
    let unsubscribe3;
    if (group.festival && group.festival.id) {
      //set up listener for festival info
      unsubscribe3 = onSnapshot(doc(db, 'festivals', group.festival.id), (doc) => {
        if (doc.data()) {
          const festivalData = doc.data();
          setFestival(festivalData);
          UserCtx.setFestival(festivalData)
          // Apply this festival's brand colours as CSS custom properties —
          // re-runs on every snapshot, so switching festivals (new group.festival.id
          // re-subscribes, since this effect depends on [group]) or an admin
          // editing the colours live both pick up correctly.
          const brandPrimary = festivalData.brandColorPrimary || '#2be494';
          const accessibleNavColour = ensureAccessibleNavColour(brandPrimary);
          document.documentElement.style.setProperty('--brand-primary', brandPrimary);
          // Secondary brand colour deactivated for now (admin's input for it
          // is hidden too, see festivals.tsx) — always the app default
          // rather than festivalData.brandColorSecondary. Not removed, just
          // not applied; restore the line above this comment to bring it back.
          document.documentElement.style.setProperty('--brand-secondary', '#2c3030');
          // Bottom nav's active icon/label colour — same as --brand-primary
          // for most festivals, but darkened just enough to stay readable
          // when a festival's brand colour is too light/bright on its own
          // (see ensureAccessibleNavColour above). Only this one UI spot
          // uses it; everything else stays at --brand-primary's full
          // vibrancy.
          document.documentElement.style.setProperty('--brand-nav', accessibleNavColour);
          // Text/icon colour for surfaces using --brand-primary directly as
          // a background (e.g. NeedScreen's swipe action buttons) — plain
          // white or black, whichever contrasts better against this
          // specific brand colour, since a mid-toned brand colour like
          // magenta/pink is too close to a darkened version of itself for
          // that darken-until-3:1 approach above to read clearly.
          document.documentElement.style.setProperty('--brand-on-primary', getAccessibleTextColour(brandPrimary));
        }
      });
    }
    // Always keep UserCtx.itemInfo populated with the master defaultitems
    // catalog — it's the fallback source GearCard/ItemDetail/NeedScreen's
    // hire/tip lookups fall through to whenever a real festival's own
    // items array is empty (useDefaultItems: true with nothing
    // customised), and the only source at all for a custom event with no
    // linked festival doc. Used to be cleared to undefined for any
    // real-festival group on the assumption that festival's items array
    // was always fully populated — broke the moment a festival ended up
    // using the shared catalog with an empty customisation array.
    const defaultItemsQuery = query(collection(db, "defaultitems"));
    const unsubscribeDefaultItems = onSnapshot(defaultItemsQuery, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((doc) => { items.push(doc.data()); });
      UserCtx.setItemInfo(items)
    });
    // Tear down this run's listeners before the next one is set up (e.g.
    // switching festivals) — without this, old listeners stay attached and
    // can fire after the new one, overwriting the current festival's brand
    // colours with a stale festival's data.
    return () => { if (unsubscribe3) unsubscribe3(); unsubscribeDefaultItems(); };
  }, [group?.festival?.id]);

  if (!group) return ( <Loading msg='Loading' />)

  if (!group.acknowledgement) return ( <Acknowledgement group={group}/>)
  
  if (!group.festival) return ( <OnboardingFestival group={group}/>)
  
  if (!group.items && !itemsLoadFailed) return ( <Loading msg='Loading' />)

    return (
      <div className='app-wrapper'>

      <TopMenu currentScreen={currentScreen} group={group} festivalInfo={festival} setCurrentScreen={goToScreen}/>

      <div className='content'>

      { currentScreen === "home" && 
        <Home 
          group={group} 
          festival={group?.festival} 
          festivalInfo = {festival}
          setCurrentScreen = {goToScreen}
          setActiveCategory = {setActiveCategory}
          /> 
      }
      
      { currentScreen === "list" &&
        <Checklist
        group={group}
        festivalInfo={festival}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        onAddItem={(cat) => { addSheetCategoryRef.current = cat || ''; setAddSheetCategory(cat || ''); setAddSheetInitialMode('gear'); setShowAddSheet(true); }}
        onShowToast={handleShowToast}
        onAddCategory={handleAddCategory}
        onRenameCategory={handleRenameCategory}
        onDeleteCategory={handleDeleteCategory}
        onSetCategoryIcon={handleSetCategoryIcon}
        onReorderCategories={handleReorderCategories}
        />
      }
      { currentScreen === "need" && <NeedScreen group={group} festivalInfo={festival} activeCategory={activeCategory} setActiveCategory={setActiveCategory} setCurrentScreen={goToScreen} onAddItem={(cat) => { addSheetCategoryRef.current = cat || ''; setAddSheetCategory(cat || ''); setAddSheetInitialMode('gear'); setShowAddSheet(true); }} onShowToast={handleShowToast} onAddCategory={handleAddCategory} onRenameCategory={handleRenameCategory} onDeleteCategory={handleDeleteCategory} onSetCategoryIcon={handleSetCategoryIcon} onReorderCategories={handleReorderCategories} onSortedToPack={handleSortedToPack}/> }
      { currentScreen === "pack" && <PackScreen group={group} festivalInfo={festival} activeCategory={activeCategory} setActiveCategory={setActiveCategory} setCurrentScreen={goToScreen} onAddItem={(cat) => { addSheetCategoryRef.current = cat || ''; setAddSheetCategory(cat || ''); setAddSheetInitialMode('gear'); setShowAddSheet(true); }} onShowToast={handleShowToast} onAddCategory={handleAddCategory} onRenameCategory={handleRenameCategory} onDeleteCategory={handleDeleteCategory} onSetCategoryIcon={handleSetCategoryIcon} onReorderCategories={handleReorderCategories}/> }
      { currentScreen === "notifications" && <Notifications settings={settings} group={group}/> }
      { currentScreen === "settings" && <Settings group={group} settings={settings}/> }

      </div>

      {showToast && (
        <div className="toast">{toastMessage}</div>
      )}
      {showAddSheet && group && <AddSheet group={group} activeCategory={addSheetCategoryRef.current} initialMode={addSheetInitialMode} onClose={() => setShowAddSheet(false)} screen={currentScreen === 'list' ? 'gear' : currentScreen} />}
      <MessageModal festival={festival} group={group} currentScreen={currentScreen} />
      <BottomTabs
        currentScreen={currentScreen}
        setCurrentScreen ={goToScreen}
        group={group}
        festivalInfo={festival}
        packBadgeCount={packSortBadge}
        packBumpTrigger={packBumpTrigger}
        />

    </div>
    );

}

export default Root;